import {matchRoutes, type NavigateOptions, type RouteObject} from '@github-ui/react-router'
import {preserveStaffNavigationParams} from './staff-navigation-params'

interface RouterLocation {
  pathname: string
  search?: string
  hash?: string
  state?: unknown
}

interface ReactRouterBlocker {
  state: 'blocked' | 'proceeding' | 'unblocked'
  location?: RouterLocation
}

export interface ReactRouterState {
  location: RouterLocation
  blockers?: Map<string, ReactRouterBlocker>
  navigation?: {
    state: 'idle' | 'loading' | 'submitting'
    location?: RouterLocation
  }
}

export interface ReactDataRouter {
  state: ReactRouterState
  subscribe: (subscriber: (state: ReactRouterState) => void) => () => void
  dispose: () => void
}

export interface NavigableReactApp extends HTMLElement {
  navigate?: (path: string, options?: NavigateOptions) => Promise<void> | void
  routerOrHistory?: unknown
  routes?: RouteObject[]
}

export interface ResolvedReactAppNavigation {
  app: NavigableReactApp
  destination: URL
  destinationPath: string
  matchedRoutes: NonNullable<ReturnType<typeof matchRoutes>>
}

interface ResolveReactAppNavigationOptions {
  app: NavigableReactApp
  destination: string
  requireLeafRoute?: boolean
}

export interface ReactAppNavigationOptions {
  state?: Record<string, unknown>
}

/** The navigation was blocked and then reset, superseded by another navigation, or never committed. */
export class ReactAppNavigationCancelledError extends Error {
  constructor() {
    super('Navigation was cancelled')
    this.name = 'ReactAppNavigationCancelledError'
  }
}

/** A loader redirected the navigation, and the router committed `location` instead of the requested destination. */
export class ReactAppNavigationRedirectedError extends Error {
  readonly location: string

  constructor(location: RouterLocation) {
    const path = `${location.pathname}${location.search ?? ''}${normalizedHash(location)}`
    super(`Navigation was redirected to ${path}`)
    this.name = 'ReactAppNavigationRedirectedError'
    this.location = path
  }
}

export function findReactApp(appName: string): NavigableReactApp | undefined {
  const app = document.querySelector<NavigableReactApp>('react-app')
  return app?.getAttribute('app-name') === appName ? app : undefined
}

export function resolveReactAppNavigation({
  app,
  destination: destinationPath,
  requireLeafRoute = false,
}: ResolveReactAppNavigationOptions): ResolvedReactAppNavigation | false {
  let destination: URL
  try {
    destination = new URL(destinationPath, window.location.href)
  } catch {
    return false
  }

  if (destination.origin !== window.location.origin) return false
  destination.search = preserveStaffNavigationParams(destination.search)

  try {
    const routes = app.routes
    if (!routes) return false

    const matchedRoutes = matchRoutes(routes, destination.pathname)
    const leafRoute = matchedRoutes?.at(-1)?.route
    if (!matchedRoutes || !leafRoute || (requireLeafRoute && leafRoute.children?.length)) return false

    return {
      app,
      destination,
      destinationPath: `${destination.pathname}${destination.search}${destination.hash}`,
      matchedRoutes,
    }
  } catch {
    return false
  }
}

// NOTE: This interacts with the router/history directly, which is not officially supported by React Router and
// could break if the underlying libraries change. Callers fall back to a Turbo or full navigation when it fails.
export function startReactAppNavigation(
  navigation: ResolvedReactAppNavigation,
  options?: ReactAppNavigationOptions,
): boolean {
  try {
    invokeNavigation(navigation, options)
    return true
  } catch {
    return false
  }
}

/**
 * Navigates a mounted React Router app to `destination` and resolves once the router commits it.
 *
 * Returns `false` without navigating when the app is missing, isn't a React data router, or doesn't own the
 * destination's leaf route. The returned promise rejects with `ReactAppNavigationCancelledError` when the
 * navigation is cancelled, superseded, handed off without committing, or the router is disposed, and with
 * `ReactAppNavigationRedirectedError` when a loader redirects it elsewhere.
 */
export function navigateToReactAppRoute(
  destination: string,
  appName: string,
  options?: ReactAppNavigationOptions,
): Promise<void> | false {
  const app = findReactApp(appName)
  if (!app) return false

  const navigation = resolveReactAppNavigation({app, destination, requireLeafRoute: true})
  if (!navigation) return false

  const router = navigation.app.routerOrHistory
  if (!isReactDataRouter(router)) return false

  return waitForReactRouterNavigation(router, navigation, options)
}

function invokeNavigation(
  navigation: ResolvedReactAppNavigation,
  options: ReactAppNavigationOptions | undefined,
): Promise<unknown> | void {
  const navigate = navigation.app.navigate
  if (!navigate) throw new Error('React app navigation is unavailable')
  return options?.state
    ? navigate(navigation.destinationPath, {state: options.state})
    : navigate(navigation.destinationPath)
}

function waitForReactRouterNavigation(
  router: ReactDataRouter,
  navigation: ResolvedReactAppNavigation,
  options: ReactAppNavigationOptions | undefined,
): Promise<void> {
  const {destination} = navigation
  const initialLocation = router.state.location

  return new Promise((resolve, reject) => {
    let settled = false
    let destinationWasBlocked = false
    let destinationWasPending = false
    let unsubscribe = () => {}
    let stopObservingDispose = () => {}
    const finish = (error?: Error) => {
      if (settled) return
      settled = true
      unsubscribe()
      stopObservingDispose()
      if (error) reject(error)
      else resolve()
    }

    const finishWithoutCommit = (location: RouterLocation) =>
      finish(
        destinationWasPending && isRedirect(location)
          ? new ReactAppNavigationRedirectedError(location)
          : new ReactAppNavigationCancelledError(),
      )

    unsubscribe = router.subscribe(state => {
      if (matchesDestination(state.location, destination)) return finish()

      const pendingLocation = state.navigation?.location
      if (pendingLocation) {
        if (matchesDestination(pendingLocation, destination)) {
          destinationWasPending = true
        } else if (!destinationWasPending || !isRedirect(pendingLocation)) {
          return finish(new ReactAppNavigationCancelledError())
        }
      } else if (
        state.navigation?.state === 'idle' &&
        (destinationWasPending || !locationsMatch(state.location, initialLocation))
      ) {
        return finishWithoutCommit(state.location)
      }

      const destinationBlocker = findDestinationBlocker(state, destination)
      if (destinationBlocker?.state === 'blocked') {
        destinationWasBlocked = true
      } else if (destinationWasBlocked && destinationBlocker?.state !== 'proceeding') {
        finish(new ReactAppNavigationCancelledError())
      }
    })
    if (settled) unsubscribe()
    else stopObservingDispose = onRouterDispose(router, () => finish(new ReactAppNavigationCancelledError()))

    const navigateAndSettle = async () => {
      try {
        await invokeNavigation(navigation, options)
      } catch (error) {
        return finish(error instanceof Error ? error : new Error('Navigation failed'))
      }

      // The navigate override can resolve without committing, e.g. when it hands the destination off to
      // Turbo or a document reload. Keep waiting only while the destination is blocked or still loading.
      const {state} = router
      if (settled || findDestinationBlocker(state, destination)?.state === 'blocked') return
      if (matchesDestination(state.location, destination)) finish()
      else if (!state.navigation || state.navigation.state === 'idle') finishWithoutCommit(state.location)
    }

    void navigateAndSettle()
  })
}

const routerDisposeListeners = new WeakMap<ReactDataRouter, Set<() => void>>()

// React Router clears its subscribers and blockers on dispose without notifying them, so a pending
// completion would otherwise never settle when the app unmounts or history moves to another app.
function onRouterDispose(router: ReactDataRouter, listener: () => void): () => void {
  let listeners = routerDisposeListeners.get(router)
  if (!listeners) {
    const registered = new Set<() => void>()
    const dispose = router.dispose
    router.dispose = () => {
      for (const notify of registered) notify()
      registered.clear()
      dispose.call(router)
    }
    routerDisposeListeners.set(router, registered)
    listeners = registered
  }

  const registered = listeners
  registered.add(listener)
  return () => registered.delete(listener)
}

function findDestinationBlocker(state: ReactRouterState, destination: URL): ReactRouterBlocker | undefined {
  return Array.from(state.blockers?.values() ?? []).find(
    blocker => blocker.location && matchesDestination(blocker.location, destination),
  )
}

function isRedirect(location: RouterLocation): boolean {
  const state = location.state
  return typeof state === 'object' && state !== null && '_isRedirect' in state && state._isRedirect === true
}

function matchesDestination(location: RouterLocation, destination: URL): boolean {
  return (
    location.pathname === destination.pathname &&
    normalizedHash(location) === destination.hash &&
    normalizedSearch(location) === destination.searchParams.toString()
  )
}

function locationsMatch(first: RouterLocation, second: RouterLocation): boolean {
  return (
    first.pathname === second.pathname &&
    normalizedHash(first) === normalizedHash(second) &&
    normalizedSearch(first) === normalizedSearch(second)
  )
}

function normalizedHash(location: RouterLocation): string {
  const hash = location.hash ?? ''
  return hash && !hash.startsWith('#') ? `#${hash}` : hash
}

function normalizedSearch(location: RouterLocation): string {
  return new URLSearchParams(location.search).toString()
}

// React Router data routers expose `routes`, `state`, `subscribe`, and `dispose`. TanStack Router has
// an incompatible `subscribe(event, listener)` signature and no `dispose`; Navigator history has no `subscribe`.
function isReactDataRouter(router: unknown): router is ReactDataRouter {
  return (
    typeof router === 'object' &&
    router !== null &&
    'routes' in router &&
    'state' in router &&
    'subscribe' in router &&
    'dispose' in router
  )
}
